
import React, { useState } from 'react';
import { SimulationConfig, FXPreset, MelodyInstrument } from './types';
import { SectionLabel } from './SectionLabel';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';
import { AUDIO } from './AudioService';

interface AudioPostProcessingSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

interface AudioPreset {
    name: string;
    settings: Partial<SimulationConfig>;
}

const PRESETS: AudioPreset[] = [
    { name: "Clean Studio", settings: { audioEcho: false, audioReverbLevel: 0.1, audioDistortionLevel: 0, audioLowPassFreq: 20000, melodyInstrument: 'GRAND_PIANO' } },
    { name: "Ethereal Dream", settings: { audioEcho: true, audioReverbLevel: 0.8, audioDistortionLevel: 0, audioDelayTime: 0.5, audioDelayFeedback: 0.6, melodyInstrument: 'ETHEREAL_CHIME', audioLowPassFreq: 3000 } },
    { name: "8-Bit Arcade", settings: { audioEcho: false, audioReverbLevel: 0, audioDistortionLevel: 25, melodyInstrument: '8BIT_LEAD', audioLowPassFreq: 12000 } },
    { name: "Underwater", settings: { audioEcho: true, audioReverbLevel: 0.5, audioLowPassFreq: 600, audioLowPassResonance: 8.0, melodyInstrument: 'PURE_SINE' } },
    { name: "Stadium Rock", settings: { audioEcho: true, audioReverbLevel: 0.4, audioDistortionLevel: 15, audioDelayTime: 0.2, melodyInstrument: 'ELECTRIC_GUITAR' } },
    { name: "Broken Radio", settings: { audioEcho: false, audioDistortionLevel: 60, audioLowPassFreq: 2000, audioLowPassResonance: 1.0, melodyInstrument: 'SYNTH_PLUCK' } },
    { name: "Cathedral", settings: { audioEcho: true, audioReverbLevel: 0.95, audioDelayTime: 0.8, audioDelayFeedback: 0.2, melodyInstrument: 'ORCHESTRA_STRINGS' } },
    { name: "Cyberpunk City", settings: { audioEcho: true, audioReverbLevel: 0.6, audioDistortionLevel: 10, audioDelayTime: 0.3, melodyInstrument: 'SYNTH_PLUCK' } },
    { name: "Wooden Toy", settings: { audioEcho: false, audioReverbLevel: 0.05, audioLowPassFreq: 8000, melodyInstrument: 'MARIMBA' } },
    { name: "Space Station", settings: { audioEcho: true, audioDelayTime: 0.6, audioDelayFeedback: 0.8, audioReverbLevel: 0.2, melodyInstrument: 'PURE_SINE' } },
    { name: "Heavy Metal", settings: { audioEcho: false, audioDistortionLevel: 80, audioReverbLevel: 0.1, melodyInstrument: 'ELECTRIC_GUITAR' } },
    { name: "Glassworks", settings: { audioEcho: true, audioReverbLevel: 0.3, audioLowPassFreq: 15000, audioLowPassResonance: 5.0, melodyInstrument: 'ETHEREAL_CHIME' } },
    { name: "Lo-Fi Beats", settings: { audioEcho: true, audioDistortionLevel: 20, audioLowPassFreq: 1500, audioReverbLevel: 0.1, melodyInstrument: 'GRAND_PIANO' } },
    { name: "Horror Movie", settings: { audioEcho: true, audioDistortionLevel: 30, audioDelayTime: 0.1, audioDelayFeedback: 0.9, melodyInstrument: 'ORCHESTRA_STRINGS', audioPitchShift: -5 } },
    { name: "Telephone", settings: { audioEcho: false, audioLowPassFreq: 1000, audioLowPassResonance: 2.0, audioDistortionLevel: 40, melodyInstrument: 'GRAND_PIANO' } },
    { name: "Windy Hill", settings: { audioEcho: true, audioReverbLevel: 0.6, audioLowPassFreq: 5000, melodyInstrument: 'FLUTE' } },
    { name: "Ping Pong", settings: { audioEcho: true, audioDelayTime: 0.25, audioDelayFeedback: 0.7, audioEchoStereoWidth: 1.0, melodyInstrument: 'SYNTH_PLUCK' } },
    { name: "Deep Bass", settings: { audioEcho: false, audioLowPassFreq: 300, audioDistortionLevel: 10, melodyInstrument: 'PURE_SINE', audioPitchShift: -12 } },
    { name: "Bright Pop", settings: { audioEcho: true, audioReverbLevel: 0.2, audioLowPassFreq: 18000, melodyInstrument: 'SYNTH_PLUCK' } },
    { name: "Cave Echo", settings: { audioEcho: true, audioDelayTime: 0.4, audioDelayFeedback: 0.5, audioReverbLevel: 0.5, melodyInstrument: 'FLUTE' } },
    { name: "Retro Synth", settings: { audioEcho: true, audioDistortionLevel: 5, audioReverbLevel: 0.3, melodyInstrument: '8BIT_LEAD' } },
    { name: "Orchestral Hall", settings: { audioEcho: false, audioReverbLevel: 0.7, melodyInstrument: 'ORCHESTRA_STRINGS' } }
];

export const AudioPostProcessingSection: React.FC<AudioPostProcessingSectionProps> = ({ config, setConfig }) => {
  const [rackTarget, setRackTarget] = useState<'MELODY' | 'FX'>('MELODY');
  const [isSongUploading, setIsSongUploading] = useState(false);

  const smartRandomize = () => {
    // Generates sensible, musical values instead of chaos
    const preset = PRESETS[Math.floor(Math.random() * PRESETS.length)];
    setConfig(p => ({
        ...p,
        ...preset.settings,
        // Add slight variations
        audioReverbLevel: Math.max(0, Math.min(1, (preset.settings.audioReverbLevel || 0) + (Math.random() - 0.5) * 0.2)),
        audioDelayTime: Math.max(0.05, Math.min(1.0, (preset.settings.audioDelayTime || 0.3) + (Math.random() - 0.5) * 0.2)),
    }));
  };

  const hardReset = () => {
      setConfig(p => ({
          ...p,
          audioReverbLevel: 0,
          audioDistortionLevel: 0,
          audioDelayTime: 0.3,
          audioDelayFeedback: 0,
          audioEcho: false,
          fxReverbLevel: 0,
          fxDistortionLevel: 0,
          fxEcho: false,
          audioLowPassFreq: 20000,
          audioLowPassResonance: 1.0,
          melodyInstrument: 'GRAND_PIANO',
          ballCollisionSoundType: 'MELODY', // Reset to normal melody
          audioPitchShift: 0
      }));
  };

  const loadPreset = (preset: AudioPreset) => {
      setConfig(p => ({
          ...p,
          ...preset.settings
      }));
  };

  const handleSongUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setIsSongUploading(true);
      const success = await AUDIO.uploadSongForSlicing(file);
      setIsSongUploading(false);
      
      if (success) {
          setConfig(p => ({
              ...p,
              ballCollisionSoundType: 'SONG_SLICE',
              melodyVolume: 1.0, // Ensure volume is up
              audioEcho: false, // Cleaner playback
              audioReverbLevel: 0.1 // Slight room feel
          }));
          alert("Song Loaded! Ball bounces now play the song slice-by-slice.");
      } else {
          alert("Failed to load song. Please try a valid MP3/WAV.");
      }
  };

  const isM = rackTarget === 'MELODY';
  const instruments: MelodyInstrument[] = [
      'GRAND_PIANO', 'SYNTH_PLUCK', 'ETHEREAL_CHIME', '8BIT_LEAD', 
      'ORCHESTRA_STRINGS', 'ELECTRIC_GUITAR', 'FLUTE', 'MARIMBA', 'PURE_SINE'
  ];

  return (
    <div className="flex flex-col gap-3 p-4 rounded-3xl border bg-emerald-500/5 border-emerald-500/20">
      <SectionLabel icon="fa-wand-magic-sparkles" darkMode={config.darkMode}>Studio Engine</SectionLabel>
      
      {/* QUICK ACTIONS */}
      <div className="grid grid-cols-2 gap-2 mb-2">
        <button onClick={smartRandomize} className="py-2 bg-yellow-600 text-white rounded-xl font-black text-[9px] uppercase hover:scale-105 transition-all shadow-md">
            <i className="fa-solid fa-dice mr-1"></i> Smart Random
        </button>
        <button onClick={hardReset} className="py-2 bg-red-600 text-white rounded-xl font-black text-[9px] uppercase hover:scale-105 transition-all shadow-md">
            <i className="fa-solid fa-power-off mr-1"></i> Clean Reset
        </button>
      </div>

      {/* SONG SLICER UPLOAD */}
      <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-600/10 to-purple-600/10 border border-blue-500/20 mb-2">
          <div className="flex justify-between items-center mb-2">
              <span className="text-[10px] font-black uppercase text-blue-500"><i className="fa-solid fa-scissors mr-1"></i> Real Song Slicer</span>
              {config.ballCollisionSoundType === 'SONG_SLICE' && <span className="text-[8px] bg-green-500 text-white px-2 rounded-full font-bold">ACTIVE</span>}
          </div>
          <label className={`w-full py-3 rounded-xl border-2 border-dashed flex items-center justify-center gap-2 cursor-pointer transition-all ${config.darkMode ? 'border-white/20 hover:bg-white/5' : 'border-black/10 hover:bg-black/5'}`}>
              <i className={`fa-solid ${isSongUploading ? 'fa-spinner fa-spin' : 'fa-upload'}`}></i>
              <span className="text-[9px] font-black uppercase">{isSongUploading ? 'Processing...' : 'Upload MP3 to Slice'}</span>
              <input type="file" accept="audio/*" onChange={handleSongUpload} className="hidden" />
          </label>
          <div className="mt-2 flex gap-2">
             <div className="flex-1">
                <SmartInput label="Slice Length (sec)" min={0.05} max={1.0} step={0.01} value={config.songSliceDuration || 0.15} onChange={v => setConfig(p => ({...p, songSliceDuration: v}))} darkMode={config.darkMode} />
             </div>
             <button 
                onClick={() => AUDIO.previewSlice(config)}
                className={`px-4 mt-5 rounded-xl border font-black text-[9px] uppercase transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 hover:bg-neutral-700 text-blue-400' : 'bg-white border-neutral-200 hover:bg-neutral-50 text-blue-600'}`}
             >
                <i className="fa-solid fa-play mr-1"></i> Preview
             </button>
          </div>
          <p className="text-[8px] opacity-60 mt-1 italic text-center">Bounces play the next chunk of the song.</p>
      </div>

      <SectionLabel icon="fa-sliders" darkMode={config.darkMode}>Preset Library (20+)</SectionLabel>
      <div className="grid grid-cols-2 gap-1 mb-4 max-h-32 overflow-y-auto scrollbar-hide p-1 bg-black/5 rounded-xl border border-white/5">
          {PRESETS.map(preset => (
              <button 
                key={preset.name}
                onClick={() => loadPreset(preset)}
                className={`text-[8px] font-bold py-2 px-2 rounded-lg text-left truncate transition-all hover:bg-emerald-500/20 ${config.darkMode ? 'text-white' : 'text-neutral-800'}`}
              >
                  {preset.name}
              </button>
          ))}
      </div>

      <SectionLabel icon="fa-sliders" darkMode={config.darkMode}>Manual Rack Controls</SectionLabel>
      <div className={`grid grid-cols-2 gap-1 p-1 rounded-xl mb-2 ${config.darkMode ? 'bg-neutral-800' : 'bg-neutral-100'}`}>
        <button onClick={() => setRackTarget('MELODY')} className={`text-[9px] font-black py-2 rounded-lg transition-all ${isM ? 'bg-emerald-600 text-white shadow-sm' : 'text-neutral-500'}`}>Melody Rack</button>
        <button onClick={() => setRackTarget('FX')} className={`text-[9px] font-black py-2 rounded-lg transition-all ${!isM ? 'bg-indigo-600 text-white shadow-sm' : 'text-neutral-500'}`}>Collision Rack</button>
      </div>

      <div className={`flex flex-col gap-3 p-3 rounded-2xl bg-black/10 border ${isM ? 'border-emerald-500/20' : 'border-indigo-500/20'}`}>
        {isM && (
            <ControlGroup label="Instrument">
                <select value={config.melodyInstrument || 'GRAND_PIANO'} onChange={e => setConfig(p => ({...p, melodyInstrument: e.target.value as MelodyInstrument}))} className={`w-full px-2 py-1.5 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}>
                    {instruments.map(inst => <option key={inst} value={inst}>{inst.replace('_', ' ')}</option>)}
                </select>
            </ControlGroup>
        )}

        <div className="flex items-center justify-between">
          <span className={`text-[10px] font-black uppercase ${isM ? 'text-emerald-400' : 'text-indigo-400'}`}>Stereo Echo</span>
          <Toggle 
            active={isM ? config.audioEcho : config.fxEcho} 
            onClick={() => setConfig(p => ({...p, [isM ? 'audioEcho' : 'fxEcho']: !p[isM ? 'audioEcho' : 'fxEcho']}))} 
            color={isM ? "bg-emerald-600" : "bg-indigo-600"} 
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SmartInput label="Time" min={0.05} max={1.5} step={0.01} value={isM ? config.audioDelayTime : config.fxDelayTime} onChange={v => setConfig(p => ({...p, [isM ? 'audioDelayTime' : 'fxDelayTime']: v}))} darkMode={config.darkMode} />
          <SmartInput label="Feedback" min={0} max={0.9} step={0.01} value={isM ? config.audioDelayFeedback : config.fxDelayFeedback} onChange={v => setConfig(p => ({...p, [isM ? 'audioDelayFeedback' : 'fxDelayFeedback']: v}))} darkMode={config.darkMode} />
        </div>
        <SmartInput label="Wet Mix" min={0} max={1} step={0.01} value={isM ? config.audioEchoWet : config.fxEchoWet} onChange={v => setConfig(p => ({...p, [isM ? 'audioEchoWet' : 'fxEchoWet']: v}))} darkMode={config.darkMode} />
        
        <div className="h-px bg-white/5 my-1" />
        <span className="text-[9px] font-black uppercase opacity-40">Filter & Space</span>
        <div className="grid grid-cols-2 gap-3">
          <SmartInput label="LPF Hz" min={200} max={20000} step={100} value={isM ? config.audioLowPassFreq : config.fxLowPassFreq} onChange={v => setConfig(p => ({...p, [isM ? 'audioLowPassFreq' : 'fxLowPassFreq']: v}))} darkMode={config.darkMode} />
          <SmartInput label="Resonance" min={0.1} max={20} step={0.1} value={isM ? config.audioLowPassResonance : config.fxLowPassResonance} onChange={v => setConfig(p => ({...p, [isM ? 'audioLowPassResonance' : 'fxLowPassResonance']: v}))} darkMode={config.darkMode} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SmartInput label="Reverb" min={0} max={1} step={0.01} value={isM ? config.audioReverbLevel : config.fxReverbLevel} onChange={v => setConfig(p => ({...p, [isM ? 'audioReverbLevel' : 'fxReverbLevel']: v}))} darkMode={config.darkMode} />
          <SmartInput label="Distort" min={0} max={100} step={1} value={isM ? config.audioDistortionLevel : config.fxDistortionLevel} onChange={v => setConfig(p => ({...p, [isM ? 'audioDistortionLevel' : 'fxDistortionLevel']: v}))} darkMode={config.darkMode} />
        </div>
      </div>
    </div>
  );
};